import {
  createDocChatTurn,
  getDocChatTurnStatus,
  hasDocChatConversationEvents,
  hasInProgressDocChatTurn,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import type {
  DocChatQueuedMessage,
  DocChatTurn,
  SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'

type SearchState = SearchHistoryItem['searchState']

function hasRunningTurn(turns: DocChatTurn[]): boolean {
  return (
    hasInProgressDocChatTurn(turns) ||
    turns.some(
      (turn) =>
        (turn.requestStarted || turn.restoredRunning) &&
        getDocChatTurnStatus(turn) === 'loading'
    )
  )
}

function startMessage(
  searchState: SearchState,
  siteId: string,
  message: DocChatQueuedMessage
): Partial<SearchState> {
  const docChat =
    searchState.docChat?.siteId === siteId ? searchState.docChat : undefined
  const conversation = docChat?.conversation.turns.every(
    hasDocChatConversationEvents
  )
    ? docChat.conversation
    : undefined
  return {
    query: message.query,
    browseAll: false,
    docChat: {
      ...docChat,
      siteId,
      isHydratedHistory: false,
      conversation: {
        ...conversation,
        activeTurnId: message.id,
        turns: [
          ...(conversation?.turns ?? []),
          createDocChatTurn({ ...message, turnId: message.id }),
        ],
      },
    },
  }
}

export function submitDocChatMessage(
  searchState: SearchState,
  siteId: string,
  message: DocChatQueuedMessage
): Partial<SearchState> {
  const docChat = searchState.docChat
  if (
    docChat?.siteId === siteId &&
    (hasRunningTurn(docChat.conversation.turns) ||
      docChat.queuedMessages?.length)
  ) {
    return {
      docChat: {
        ...docChat,
        queuedMessages: [...(docChat.queuedMessages ?? []), message],
      },
    }
  }
  return startMessage(searchState, siteId, message)
}

export function getNextDocChatQueuedMessage(
  searchState: SearchState,
  siteId: string
): DocChatQueuedMessage | undefined {
  const docChat = searchState.docChat
  if (
    searchState.searchMode !== 'ai' ||
    searchState.contentFilter !== 'documents' ||
    docChat?.siteId !== siteId ||
    !docChat.queuedMessages?.length ||
    docChat.queuedMessages.some((message) => message.isEditing) ||
    hasRunningTurn(docChat.conversation.turns)
  ) {
    return undefined
  }
  return docChat.queuedMessages[0]
}

export function drainDocChatMessageQueue(
  searchState: SearchState,
  siteId: string
): Partial<SearchState> {
  const message = getNextDocChatQueuedMessage(searchState, siteId)
  if (!message) return {}
  const next = startMessage(searchState, siteId, message)
  return {
    ...next,
    docChat: next.docChat && {
      ...next.docChat,
      queuedMessages: searchState.docChat?.queuedMessages?.slice(1),
    },
  }
}
